import { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY } from '../constants/time';
import { ICON_SIZE } from '../design-system';

const UsageLogItem = ({ log }) => {
  const { t } = useTranslation('settings');
  const [expanded, setExpanded] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const isAggregated = log.count > 1;

  // Format operation name for display
  const formatOperation = (operation) => {
    const operationMap = {
      'ai-chat': t('usageLog.aiChat'),
      'translate': t('usageLog.translation'),
      'translate-block': t('usageLog.translation'),
      'dictionary': t('usageLog.dictionary'),
      'grammar': t('usageLog.grammar'),
      'pdf_processing': t('usageLog.pdfProcessing'),
    };
    return operationMap[operation] || operation;
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
    const diffHours = Math.floor(diffMs / MS_PER_HOUR);
    const diffDays = Math.floor(diffMs / MS_PER_DAY);

    if (diffMins < 1) return t('usageLog.justNow');
    if (diffMins < 60) return t('usageLog.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('usageLog.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('usageLog.daysAgo', { count: diffDays });

    return then.toLocaleDateString();
  };

  // Fetch article titles when expanded
  useEffect(() => {
    if (!expanded || !log.article_ids || log.article_ids.length === 0) {
      return;
    }

    const fetchArticles = async () => {
      setLoadingArticles(true);
      try {
        const { data, error } = await supabase
          .from('reedle_articles')
          .select('id, title, url')
          .in('id', log.article_ids);

        if (error) throw error;
        setArticles(data || []);
      } catch (err) {
        console.error('Failed to fetch articles:', err);
      } finally {
        setLoadingArticles(false);
      }
    };

    fetchArticles();
  }, [expanded, log.article_ids]);

  return (
    <div
      className="usage-log-item"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="log-main">
        <div className="log-info">
          <span className="log-operation">
            {formatOperation(log.operation)}
            {isAggregated && <span className="log-count"> ×{log.count}</span>}
          </span>
          <span className="log-model">{log.model}</span>
        </div>
        <div className="log-right">
          <span className="log-credits">
            {log.credits_used?.toFixed(1) || '0.0'}
          </span>
          <span className="log-time">{formatRelativeTime(log.created_at)}</span>
          {expanded ? <ChevronDown size={ICON_SIZE.xs} /> : <ChevronRight size={ICON_SIZE.xs} />}
        </div>
      </div>

      {expanded && (
        <div className="log-details">
          <div className="log-detail-row">
            <span className="log-detail-label">{t('usageLog.inputTokens')}:</span>
            <span className="log-detail-value">{Math.round(log.input_token_count)?.toLocaleString() || '0'}</span>
          </div>
          <div className="log-detail-row">
            <span className="log-detail-label">{t('usageLog.outputTokens')}:</span>
            <span className="log-detail-value">{Math.round(log.output_token_count)?.toLocaleString() || '0'}</span>
          </div>
          <div className="log-detail-row">
            <span className="log-detail-label">{t('usageLog.provider')}:</span>
            <span className="log-detail-value">{log.provider}</span>
          </div>

          {/* Article Links Section */}
          {log.article_ids && log.article_ids.length > 0 && (
            <div className="log-detail-row log-articles">
              <span className="log-detail-label">{t('usageLog.articles')}:</span>
              <div className="log-article-list">
                {loadingArticles ? (
                  <span className="log-detail-value">{t('common:loading')}</span>
                ) : articles.length > 0 ? (
                  articles.map((article) => (
                    <a
                      key={article.id}
                      href={`/reader/${article.id}`}
                      className="log-article-link"
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {article.title || article.url || t('usageLog.untitled')}
                      <ExternalLink size={ICON_SIZE.xxs} />
                    </a>
                  ))
                ) : (
                  <span className="log-detail-value log-deleted-article">{t('usageLog.deletedArticle')}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageLogItem;

